import React, { useState, useEffect, useCallback } from 'react';
import { Send, Image as ImageIcon, Trash2, Mail, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('beatseek_token') || ''}`
});

const FILTERS = [
  { id: 'all', label: 'All Users' },
  { id: 'dj', label: 'DJs only' },
  { id: 'producer', label: 'Producers only' },
  { id: 'streamer', label: 'Streamers only' },
  { id: 'mod', label: 'Mods only' },
  { id: 'regular', label: 'Listeners / Regular' },
];

const inputCls = "w-full bg-gray-800/80 border border-cyan-500/30 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-400";

export default function EmailBlastTab() {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [images, setImages] = useState([]);
  const [ctaText, setCtaText] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [filter, setFilter] = useState('all');
  const [recipientCount, setRecipientCount] = useState(null);
  const [testEmail, setTestEmail] = useState('admin@thebeatseek.com');
  const [uploading, setUploading] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);
  const [history, setHistory] = useState([]);

  const loadCount = useCallback(async (f) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/email-blast/recipients?recipient_filter=${f}`, { headers: authHeaders() });
      if (r.ok) setRecipientCount((await r.json()).count);
    } catch (e) { /* noop */ }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/email-blast/history`, { headers: authHeaders() });
      if (r.ok) setHistory((await r.json()).blasts || []);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { loadCount(filter); }, [filter, loadCount]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const flash = (msg, isError = false) => {
    setStatusMsg({ msg, isError });
    setTimeout(() => setStatusMsg(null), 6000);
  };

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${BACKEND_URL}/api/admin/email-blast/upload-image`, { method: 'POST', headers: authHeaders(), body: fd });
      const d = await r.json();
      if (r.ok) setImages(prev => [...prev, d.url]);
      else flash(d.detail || 'Upload failed', true);
    } catch (e) { flash('Upload failed', true); }
    setUploading(false);
  };

  const payload = () => ({
    subject, body,
    image_urls: images,
    cta_text: ctaText.trim() || null,
    cta_url: ctaUrl.trim() || null,
    recipient_filter: filter,
    test_email: testEmail.trim() || null,
  });

  const sendTest = async () => {
    if (!subject.trim() || !body.trim()) return flash('Subject and message are required', true);
    setSendingTest(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/email-blast/test`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload())
      });
      const d = await r.json();
      if (r.ok) flash(`✅ Test sent to ${d.to} — check your inbox`);
      else flash(d.detail || 'Test send failed', true);
    } catch (e) { flash('Test send failed', true); }
    setSendingTest(false);
  };

  const sendBlast = async () => {
    if (!subject.trim() || !body.trim()) return flash('Subject and message are required', true);
    if (!confirmSend) {
      setConfirmSend(true);
      setTimeout(() => setConfirmSend(false), 6000);
      return;
    }
    setConfirmSend(false);
    setSending(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/email-blast/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload())
      });
      const d = await r.json();
      if (r.ok) {
        flash(`🚀 Blast is sending to ${d.recipient_count} users in the background`);
        setSubject(''); setBody(''); setImages([]); setCtaText(''); setCtaUrl('');
        setTimeout(loadHistory, 1500);
      } else flash(d.detail || 'Send failed', true);
    } catch (e) { flash('Send failed', true); }
    setSending(false);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="email-blast-tab">
      <div className="flex items-center gap-2 text-cyan-300">
        <Mail size={18} />
        <h4 className="font-semibold">Update Blast — announce new features to your community</h4>
      </div>

      {statusMsg && (
        <div
          data-testid="blast-status-msg"
          className={`text-sm px-4 py-2.5 rounded-lg border ${statusMsg.isError ? 'bg-red-900/40 border-red-500/50 text-red-300' : 'bg-emerald-900/30 border-emerald-500/40 text-emerald-300'}`}
        >
          {statusMsg.msg}
        </div>
      )}

      {/* Compose */}
      <div className="bg-gray-900/60 border border-cyan-500/20 rounded-xl p-5 space-y-4">
        <div>
          <label className="block text-xs text-gray-400 mb-1.5 font-semibold uppercase tracking-wide">Subject</label>
          <input data-testid="blast-subject-input" className={inputCls} value={subject} onChange={e => setSubject(e.target.value)} placeholder="🚀 BEATSEEK just got better — here's what's new" />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1.5 font-semibold uppercase tracking-wide">Message <span className="normal-case font-normal">(use **bold**, blank line = new paragraph, links auto-format)</span></label>
          <textarea data-testid="blast-body-input" className={`${inputCls} min-h-[160px]`} value={body} onChange={e => setBody(e.target.value)} placeholder={"Hey Beatseekers!\n\nWe just shipped some fresh updates:\n\n**Mobile search overhaul** — full-screen results with back-to-results\n**Notification hub** — follow back, filters and more"} />
        </div>

        {/* Images */}
        <div>
          <label className="block text-xs text-gray-400 mb-1.5 font-semibold uppercase tracking-wide">Images (optional)</label>
          <div className="flex flex-wrap items-center gap-3">
            {images.map((img, i) => (
              <div key={i} className="relative group">
                <img src={`${BACKEND_URL}${img}`} alt="blast" className="h-16 w-24 object-cover rounded-lg border border-cyan-500/30" />
                <button
                  data-testid={`blast-image-remove-${i}`}
                  onClick={() => setImages(prev => prev.filter((_, x) => x !== i))}
                  className="absolute -top-2 -right-2 bg-red-600 rounded-full p-1 text-white"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
            <label className="cursor-pointer flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-cyan-500/40 text-cyan-300 text-xs hover:bg-cyan-500/10">
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
              {uploading ? 'Uploading...' : 'Add image'}
              <input data-testid="blast-image-input" type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && uploadImage(e.target.files[0])} />
            </label>
          </div>
        </div>

        {/* CTA */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1.5 font-semibold uppercase tracking-wide">Button text (optional)</label>
            <input data-testid="blast-cta-text" className={inputCls} value={ctaText} onChange={e => setCtaText(e.target.value)} placeholder="Check it out" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5 font-semibold uppercase tracking-wide">Button link</label>
            <input data-testid="blast-cta-url" className={inputCls} value={ctaUrl} onChange={e => setCtaUrl(e.target.value)} placeholder="https://thebeatseek.com" />
          </div>
        </div>

        {/* Recipients */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1.5 font-semibold uppercase tracking-wide">Recipients</label>
            <select data-testid="blast-filter-select" className={inputCls} value={filter} onChange={e => setFilter(e.target.value)}>
              {FILTERS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <span data-testid="blast-recipient-count" className="text-sm text-cyan-300 pb-2">
              {recipientCount === null ? 'Counting...' : `📬 ${recipientCount} recipients`}
            </span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-gray-900/60 border border-cyan-500/20 rounded-xl p-5 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <label className="block text-xs text-gray-400 mb-1.5 font-semibold uppercase tracking-wide">Test email address</label>
            <input data-testid="blast-test-email" className={inputCls} value={testEmail} onChange={e => setTestEmail(e.target.value)} />
          </div>
          <button
            data-testid="blast-send-test-btn"
            onClick={sendTest}
            disabled={sendingTest}
            className="px-4 py-2 rounded-lg bg-cyan-600/20 border border-cyan-500/50 text-cyan-300 text-sm font-semibold hover:bg-cyan-600/30 disabled:opacity-50 whitespace-nowrap"
          >
            {sendingTest ? 'Sending...' : '✉️ Send me a test'}
          </button>
        </div>
        <button
          data-testid="blast-send-btn"
          onClick={sendBlast}
          disabled={sending}
          className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-bold transition-colors disabled:opacity-50 ${confirmSend ? 'bg-red-600 text-white' : 'bg-gradient-to-r from-red-600/80 to-red-700/80 text-white hover:from-red-600 hover:to-red-700'}`}
        >
          <Send size={15} />
          {sending ? 'Dispatching...' : confirmSend ? `⚠️ Confirm — send to ${recipientCount ?? '?'} users?` : `Send Blast to ${recipientCount ?? '...'} users`}
        </button>
      </div>

      {/* History */}
      <div className="bg-gray-900/60 border border-cyan-500/20 rounded-xl p-5">
        <h5 className="text-sm font-semibold text-gray-300 mb-3">📜 Send History</h5>
        {history.length === 0 ? (
          <p className="text-xs text-gray-500">No blasts sent yet.</p>
        ) : (
          <div className="space-y-2">
            {history.map(b => (
              <div key={b.id} data-testid={`blast-history-${b.id}`} className="flex items-center justify-between gap-3 text-xs bg-gray-800/50 rounded-lg px-3 py-2 border border-white/5">
                <div className="min-w-0">
                  <div className="text-white font-semibold truncate">{b.subject}</div>
                  <div className="text-gray-500">{new Date(b.created_at).toLocaleString()} · {b.recipient_filter} · {b.recipient_count} recipients</div>
                </div>
                <span className={`shrink-0 px-2 py-0.5 rounded-full font-semibold ${b.status === 'completed' ? 'bg-emerald-900/50 text-emerald-300' : b.status === 'sending' ? 'bg-amber-900/50 text-amber-300' : 'bg-red-900/50 text-red-300'}`}>
                  {b.status === 'completed' ? `✓ ${b.sent_count} sent${b.failed_count ? ` · ${b.failed_count} failed` : ''}` : b.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
